import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../context/globalContext";
import { deleteAccountAPI, exportMyDataAPI } from "../../api/auth";
import { setAuthToken } from "../../api/index";
import Spinner from "../../components/Spinner/Spinner";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

export default function UserPagePreferences() {
  const [deleteText, setDeleteText] = useState("");
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { currentUser, setCurrentUser } = useContext(GlobalContext);
  const navigate = useNavigate();

  const canSubmitDelete =
    Boolean(currentUser?.email) &&
    deleteText === "DELETE" &&
    password.length > 0 &&
    !isDeleting;

  async function handleDeleteUser(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!canSubmitDelete) {
      setErrorMessage(
        "Please type DELETE and enter your password to confirm.",
      );
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteAccountAPI({
        password,
        reason: reason.trim() || undefined,
      });
      if (result.ok) {
        setAuthToken(null);
        setCurrentUser(null);
        localStorage.removeItem("lineItems");
        setSuccessMessage("Account deleted. Goodbye!");
        setTimeout(() => navigate("/"), 1500);
      } else {
        setErrorMessage(result.message ?? "Failed to delete account.");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleExportData() {
    setErrorMessage("");
    setSuccessMessage("");
    setIsExporting(true);
    try {
      const blob = await exportMyDataAPI();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `eventshouse-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(
        getUserFacingErrorMessage(
          err,
          "Failed to export your data. Please try again later.",
        ),
      );
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="user-page-preferences-container">
      <h2 className="font-bold text-lg text-slate-900 mb-6">Preferences</h2>

      <section className="mt-4 mb-8">
        <h3 className="font-semibold mb-2">Export my data</h3>
        <p className="text-sm mb-3">
          Download a copy of everything we store about you (profile, orders,
          payments, contact submissions) as a JSON file.
        </p>
        <button
          type="button"
          onClick={handleExportData}
          disabled={isExporting}
          className="bg-slate-200 px-3 py-1 text-sm rounded-sm"
        >
          {isExporting ? <Spinner /> : "Export my data (JSON)"}
        </button>
      </section>

      <hr className="my-6" />

      <section>
        <h3 className="font-semibold mb-2">DELETE account</h3>
        <p className="text-sm mb-3">
          Your account will be deactivated immediately. You will be logged out
          on all your devices. Past orders remain associated with your email
          address for accounting and compliance, and your record is permanently
          purged after 1 year. This action cannot be undone.
        </p>

        <div className="user-page-delete-container flex flex-col gap-2 max-w-md">
          <label className="text-sm">
            Type <code>DELETE</code> to confirm:
            <input
              type="text"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              className="block mt-1 border rounded px-2 py-1 w-full"
            />
          </label>
          <label className="text-sm">
            Confirm your password:
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block mt-1 border rounded px-2 py-1 w-full"
            />
          </label>
          <label className="text-sm">
            Optional: tell us why you are leaving:
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              maxLength={500}
              className="block mt-1 border rounded px-2 py-1 w-full"
              rows={2}
            />
          </label>
          <button
            type="button"
            onClick={handleDeleteUser}
            disabled={!canSubmitDelete}
            className="bg-red-700 text-white px-3 py-1 rounded mt-2 disabled:opacity-50"
          >
            {isDeleting ? <Spinner /> : "Permanently delete my account"}
          </button>
        </div>
      </section>

      {errorMessage && (
        <ErrorAlert className="mt-4">{errorMessage}</ErrorAlert>
      )}
      {successMessage && <p className="text-green-700 mt-4">{successMessage}</p>}
    </div>
  );
}
